import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { PaginationService } from "@shared/components/pagination/_services/pagination.service";
import { PaginationOptions } from "@shared/constants/pagination";
import { LegalEntityService } from "@shared/services/legal-entity/legal-entity.service";
import { PaygroupsService } from "@shared/services/paygroups/paygroups.service";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { PaginationData } from "@store/models/pagination.model";
import { Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, finalize, take, takeUntil } from "rxjs/operators";
import { PayGroup } from "src/app/shared/models/pay-groups";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import { CustomerEntityGroupSelect } from "src/app/store/models/customerEnitityGroupSelection.model";
import { getCustomerEntityGroupState, getGlobalDashboardFilterState, getPagination } from "../../../../store";
import { AppState } from "../../../../store/models/state.model";

import { CountriesService } from "@shared/services/countries/countries.service";
import { IDashboardFiltersFormGroup } from "@modules/global-dashboard/models/global-dashboard-interface";
import { LegalEntity } from "@shared/models/legal-entity.interface";
import { Country } from "@shared/models/country.interface";

@Component({
	selector: "app-pay-elective-pay-groups",
	templateUrl: "./pay-elective-pay-groups.component.html",
	styleUrls: ["./pay-elective-pay-groups.component.scss"]
})
export class PayElectivePayGroupsComponent implements OnInit, OnDestroy {
	legalEntitySelectionOptions$!: Observable<SelectOption[]>;
	destroy$ = new Subject<void>();

	pageIndex = 0;
	pageSize = 10;
	totalPageCount!: number;
	form!: FormGroup;
	entitySelectForm!: FormGroup;

	private isClientRole = false;

	readonly ALL_OPTION: SelectOption = {
		value: "ALL_OPTIONS",
		text: "All legal entities"
	};

	customerHasPayGroups = true;
	customerName = "";

	busy = true;
	countries: Country[] = [];
	legalEntitiesSelectOptions: SelectOption[] = [];
	payGroupsList: PayGroup[] = [];
	clientLegalEntityIdsInSelectedCountry: string[] = [];
	clientCountryList: SelectOption[] = [];
	clientSelectedCountry = "";
	clientSelectedCountryFlag = "";
	gdFilters!: FormGroup;
	gdLegalEntityId = "";

	customerId = "";
	legalEntityIds: string[] = [];
	allLegalEntityOptions: string[] = [];
	canSeeMap = false;
	queryString = "";
	paginationType: PaginationOptions = PaginationOptions.PAYGROUPS;

	constructor(
		private router: Router,
		private store: Store<AppState>,
		private legalEntityService: LegalEntityService,
		private payGroupsService: PaygroupsService,
		private permissions: PermissionsService,
		private paginationService: PaginationService,
		private countriesService: CountriesService
	) {
		this.permissions
			.canSeeMapAndIsClient()
			.pipe(takeUntil(this.destroy$))
			.subscribe((res: { isClient: boolean; canSeeMap: boolean }) => {
				this.canSeeMap = res.canSeeMap;
				this.isClientRole = res.isClient;
			});

		this.countriesService
			.getCountries()
			.pipe(take(1))
			.subscribe({
				next: countries => {
					this.countries = countries;
				}
			});
	}

	ngOnInit(): void {
		this.initalizeComponentData();
	}

	initalizeComponentData(): void {
		if (this.isClientRole) this.fetchGDFiltersFromState();
		this.fetchCustomerFromState();
		this.setupPaginationSubscription();
		this.initForm();
	}

	fetchCustomerFromState(): void {
		this.store
			.pipe(takeUntil(this.destroy$), select(getCustomerEntityGroupState))
			.subscribe((state: CustomerEntityGroupSelect) => {
				if (state?.customerId) {
					this.customerName = state.customerName!;
					this.customerId = state.customerId;
					this.isClientRole ? this.getClientCountryBreadcrumbInfo() : this.getLegalEntities(this.customerId);
				}
			});
	}

	fetchGDFiltersFromState(): void {
		this.gdFilters = new FormGroup({
			customers: new FormControl([]),
			legalEntities: new FormControl([]),
			paygroups: new FormControl([]),
			statuses: new FormControl([]),
			groups: new FormControl([]),
			deadline: new FormControl(""),
			milestoneStartDate: new FormControl(""),
			milestoneTypes: new FormControl([]),
			process: new FormGroup({
				automatic: new FormControl(false),
				manual: new FormControl(false)
			})
		}) as IDashboardFiltersFormGroup;

		this.store.pipe(takeUntil(this.destroy$), select(getGlobalDashboardFilterState)).subscribe(state => {
			if (state && state.globalDashboard && state.globalDashboard.filters) {
				this.gdFilters.patchValue(state.globalDashboard.filters);

				this.gdLegalEntityId = Array.isArray(this.gdFilters.get("legalEntities")?.value)
					? this.gdFilters.get("legalEntities")?.value[0] || ""
					: this.gdFilters.get("legalEntities")?.value || "";

				this.getClientCountryBreadcrumbInfo();
			}
		});
	}

	setupPaginationSubscription(): void {
		this.store.pipe(select(getPagination), takeUntil(this.destroy$)).subscribe({
			next: pagination => {
				if (
					pagination &&
					pagination.hasOwnProperty("PAYGROUPS") &&
					this.customerId &&
					this.legalEntityIds.length
				) {
					const paygroupPagination = pagination["PAYGROUPS"];
					this.pageSize = paygroupPagination.pageSize;
					this.pageIndex = paygroupPagination.pageIndex;

					this.getPayGroups(
						[this.customerId],
						this.legalEntityIds,
						paygroupPagination.pageSize,
						paygroupPagination.pageIndex,
						this.queryString
					);
				}
			}
		});
	}

	getLegalEntities(customerId: string): void {
		this.legalEntityIds = [];
		this.legalEntityService
			.getLegalEntitiesForCustomerAsSelectOption(customerId, true)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe(legalEntities => {
				if (!legalEntities.length) return;

				if (this.isClientRole && this.clientSelectedCountry != "") {
					this.allLegalEntityOptions = this.clientLegalEntityIdsInSelectedCountry;
					this.legalEntitiesSelectOptions = legalEntities.filter(entity =>
						this.clientLegalEntityIdsInSelectedCountry.includes(entity.value)
					);
				} else {
					this.allLegalEntityOptions = legalEntities.map(entity => entity.value);
					this.legalEntitiesSelectOptions = legalEntities;
				}

				this.legalEntityIds = [...this.allLegalEntityOptions];
				this.legalEntitiesSelectOptions.unshift(this.ALL_OPTION);
				this.clearForm();

				this.getPayGroups([this.customerId], this.legalEntityIds, this.pageSize, 0, this.queryString);
			});
	}

	getPayGroups(
		customerIds: string[],
		legalEntityIds: string[],
		pageSize: number,
		pageIndex: number,
		queryString: string
	): void {
		this.busy = true;
		const statuses: string = this.isClientRole ? "LIVE" : "LIVE, IMPLEMENTATION";
		legalEntityIds = this.payGroupsService.searchPaygroupInLargeEntities(legalEntityIds);

		this.payGroupsService
			.getPayGroups(customerIds, legalEntityIds, statuses, pageSize, pageIndex, queryString)
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				finalize(() => (this.busy = false))
			)
			.subscribe(payGroups => {
				this.totalPageCount = payGroups.totalPages;
				this.payGroupsList = payGroups.items;
			});
	}

	getClientCountryBreadcrumbInfo(): void {
		this.legalEntityService
			.getLegalEntitiesForCustomer(this.customerId)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe(legalEntities => {
				const clientLegalEntities = legalEntities.items;

				this.generateClientCountryList(clientLegalEntities);

				//Get Country Flag
				this.clientSelectedCountryFlag = this.legalEntitiesSelectOptions.filter(
					f => f.value == this.gdLegalEntityId
				)[0]?.imagePath as "";

				//Get Country Name
				this.clientSelectedCountry = "";
				let clientSelectedCountryId = "";
				this.clientCountryList.forEach(legalEntity => {
					if (legalEntity.valueArray?.includes(this.gdLegalEntityId)) {
						this.clientSelectedCountry = legalEntity.text;
						clientSelectedCountryId = legalEntity.value;
					}
				});

				//Get Legal Entities in Country
				this.clientLegalEntityIdsInSelectedCountry = clientLegalEntities
					.filter(cle => cle.data.country === clientSelectedCountryId)
					.map(cle => cle.id);

				this.getLegalEntities(this.customerId);
			});
	}

	generateClientCountryList(clientLegalEntities: LegalEntity[]): void {
		this.clientCountryList = [];
		clientLegalEntities.forEach(legalEntity => {
			if (legalEntity.payGroupCount == 0) return;

			const existingIndex: number = this.clientCountryList.findIndex(
				(country: SelectOption) => country.value === legalEntity.data.country
			);

			if (existingIndex === -1) {
				this.clientCountryList.push(
					this.countriesService.filterCountryByCode(this.countries, legalEntity.data.country)
				);

				this.clientCountryList[this.clientCountryList.length - 1].valueArray = [legalEntity.id];
			} else {
				this.clientCountryList[existingIndex].valueArray?.push(legalEntity.id);
			}
		});
	}

	initForm(): void {
		this.entitySelectForm = new UntypedFormGroup({
			legal: new UntypedFormControl("", Validators.required),
			paygroup: new UntypedFormControl("")
		});

		this.setupValueChanges();
	}

	clearForm(): void {
		this.queryString = "";
		this.entitySelectForm.reset();
	}

	setupValueChanges(): void {
		this.entitySelectForm
			.get("legal")
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((legalEntity: string) => {
				this.payGroupsList = [];
				if (legalEntity) {
					legalEntity === "ALL_OPTIONS"
						? (this.legalEntityIds = [...this.allLegalEntityOptions])
						: (this.legalEntityIds = [legalEntity]);

					this.resetPagination();
				}
			});

		this.entitySelectForm
			.get("paygroup")
			?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe((search: string) => {
				if (search !== null) {
					this.queryString = search;
					this.resetPagination();
				}
			});
	}

	resetPagination(): void {
		const paginationSelection: PaginationData = {
			pageSize: this.pageSize,
			pageIndex: 0
		};

		this.paginationService.updatePaginationSate(this.paginationType, paginationSelection);
	}

	resetOptions(): void {
		this.legalEntitiesSelectOptions = [];
		this.payGroupsList = [];
		this.legalEntityIds = [];
		this.resetPagination();

		if (this.entitySelectForm) {
			this.entitySelectForm.reset();
		}
	}

	navigateToEntitySetup(): void {
		this.router.navigate(["/legal-entities"]);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
